import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod/v3'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { StoreCombobox } from '~/components/new/app-ui/store-combobox'
import { Title } from '~/components/new/app-ui/title'
import { useRememberedStore } from '~/components/new/app-ui/use-remembered-store'
import { CameraConfigPage } from '~/components/routes/camera-config/camera-config-page'
import { CAMERA_CONFIG_READ } from '~/components/routes/cameras/permissions'
import { Button } from '~/components/ui/button'
import { withRoutePermissions } from '~/utils/rbac'

const STORE_STORAGE_KEY = 'cameras-config'

export const Route = createFileRoute('/_authenticated/cameras/config')({
  staticData: {
    permission: CAMERA_CONFIG_READ,
  },
  component: RouteComponent,
  validateSearch: z.object({
    storeId: z.number().optional(),
    returnTo: z.enum(['legacy', 'new']).optional(),
  }),
})
withRoutePermissions(Route)

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { storeId: searchStoreId, returnTo } = Route.useSearch()
  const { remember, remembered } = useRememberedStore(STORE_STORAGE_KEY)
  const [isVpnDirty, setIsVpnDirty] = useState(false)
  const [isOrderDirty, setIsOrderDirty] = useState(false)

  // A shared link carries its own store; the memory only fills in when the screen arrives without.
  const storeId = searchStoreId ?? (typeof remembered === 'number' ? remembered : undefined)

  // Des modifications non enregistrées (VPN, ordre des caméras) sont confirmées avant de changer.
  const handleStoreChange = (nextStoreId: number | null) => {
    const normalizedStoreId = nextStoreId ?? undefined

    if (normalizedStoreId === storeId) {
      return
    }

    const unsaved = [isVpnDirty && 'configuration VPN', isOrderDirty && 'ordre des caméras'].filter(
      Boolean,
    )
    if (
      unsaved.length > 0 &&
      !window.confirm(
        `Des modifications non enregistrées (${unsaved.join(', ')}) seront perdues. Continuer ?`,
      )
    ) {
      return
    }

    remember(nextStoreId)
    navigate({
      to: '/cameras/config',
      search: { storeId: normalizedStoreId, returnTo },
    })
  }

  return (
    <>
      <AppHeader>
        <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <Title>Camera Config</Title>

          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
            <StoreCombobox onChange={handleStoreChange} value={storeId ?? null} />
            <Button
              variant="outline"
              onClick={() => {
                if (returnTo === 'new') {
                  navigate({ to: '/cameras/new', search: { storeId, mode: 'boutique' } })
                } else {
                  navigate({ to: '/cameras', search: { storeId } })
                }
              }}
            >
              <ArrowLeft />
              Retour aux caméras
            </Button>
          </div>
        </div>
      </AppHeader>

      <AppContainer className="gap-6">
        <CameraConfigPage
          onOrderDirtyChange={setIsOrderDirty}
          onVpnDirtyChange={setIsVpnDirty}
          storeId={storeId}
        />
      </AppContainer>
    </>
  )
}
